/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useDxpFrameStore, useIdentityGatewayServiceStore } from 'dxpConsoleCore-mf/State';
import React, { useEffect } from 'react';
import { DxpCapability } from '../../Constants/DxpCapabilities/defs/DxpCapability.defs';
import { getCapability } from '../../Utilities/GetCapability/GetCapability';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { DxpFrameProps } from './DxpFrame.props';
import { FrameBody } from './elements/FrameBody/FrameBody';
import { FrameBodyProps } from './elements/FrameBody/FrameBody.props';
import { FrameHeader } from './elements/FrameHeader/FrameHeader';
import { FrameHeaderProps } from './elements/FrameHeader/FrameHeader.props';
import { FrameMenu } from './elements/FrameMenu/FrameMenu';

/**
 * DXP Console Frame.
 * @param {DxpFrameProps} props Props passed to `<DxpFrame />`.
 * @export
 * @returns {JSX.Element} The `<DxpFrame />` component.
 */
export const DxpFrame = (props: DxpFrameProps): JSX.Element => {
  const { applicationIA, toolbarProps } = props;

  const { hasSession }: { hasSession: boolean } = useIdentityGatewayServiceStore();
  const { updateCapability } = useDxpFrameStore();
  const { capability }: { capability: DxpCapability | null } = useDxpFrameStore();

  const derivedCapability = getCapability();

  // Configure settings for elements
  const frameHeaderProps: FrameHeaderProps = { toolbarProps };
  const frameBodyProps: FrameBodyProps = { applicationIA };

  useEffect(() => {
    // Don't try to do anything if the user is not logged in
    if (!hasSession) {
      return;
    }

    // Figure out if we have a new capability to set
    let shouldUpdateCapability = false;

    if (capability) {
      // A capability already exists.
      // If a different capability was found, update it.
      if (capability.key !== derivedCapability.key) {
        shouldUpdateCapability = true;
      }
    } else {
      // No capability has been set.
      shouldUpdateCapability = true;
    }

    if (shouldUpdateCapability) {
      updateCapability(derivedCapability);
    }
  }, [capability, derivedCapability, hasSession, updateCapability]);

  return (
    <ErrorBoundary>
      <FrameHeader {...frameHeaderProps} />
      <div className="application">
        <FrameMenu />
        <FrameBody {...frameBodyProps} />
      </div>
    </ErrorBoundary>
  );
};
